// Agents/Edit — Iverson rewrite (Phase 4).
//
// Two-column layout: main column = identity + role prompt + skills +
// runtime config (model, escalation, structured rate limits, greetings,
// auto-resolve). Right rail = rehearsal status, performance, details,
// danger zone (no Quick links per spec).
//
// All form fields autosave on blur via `useAutosave`. There is NO Save
// button. The status indicator in the page header reflects "saving",
// "saved", or "error" — flips back to idle after ~1.5s.

import { useState, type KeyboardEvent } from "react"
import { Link, router } from "@inertiajs/react"
import { toast } from "sonner"
import {
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Sparkles,
  Trash2,
  X,
} from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { AgentSkillsPanel } from "@/components/domain/agent-skills-panel"
import { AgentDesignerPane } from "@/components/domain/agent-designer-pane"
import { AvatarDropzone } from "@/components/domain/avatar-dropzone"
import { useAutosave, type AutosaveStatus } from "@/lib/use-autosave"
import { fmtRelativeTime } from "@/lib/format"
import { cn } from "@/lib/cn"
import {
  RATE_LIMIT_DEFAULTS,
  type Agent,
  type AiModelOption,
  type RateLimitKey,
} from "@/types/agent"
import type { Skill } from "@/types/skill"

type Props = {
  agent: Agent
  skills: Skill[]
  availableSkills?: Skill[]
  aiModels?: AiModelOption[]
}

export default function AgentsEdit({
  agent: initialAgent,
  skills,
  availableSkills = [],
  aiModels = [],
}: Props) {
  // Local mirror of the agent — autosave PATCH responses funnel through
  // `setAgent` so timestamps and derived chips refresh without a full
  // Inertia visit.
  const [agent, setAgent] = useState<Agent>(initialAgent)
  const [forgeOpen, setForgeOpen] = useState(false)
  const [runtimeOpen, setRuntimeOpen] = useState(false)

  // Toast on every successful field save. The label comes from FIELD_LABELS
  // (snake_case API field → human-readable phrase). Anything not in the map
  // falls back to a generic "Saved" so we never go silent.
  const autosave = useAutosave<Agent>({
    url: `/agents/${agent.id}`,
    onSaved: (next, field) => {
      setAgent(next)
      toast.success(FIELD_LABELS[field] ?? "Saved")
    },
    onError: (msg, field) =>
      toast.error(`${FIELD_LABELS[field] ?? "Save"} failed: ${msg}`),
  })

  // ── Identity + role prompt bindings ──
  const nameBind         = autosave.bind("name",          { initial: agent.name })
  const roleLabelBind    = autosave.bind("role_label",    { initial: agent.roleLabel })
  const voiceBind        = autosave.bind("voice_and_tone",{ initial: agent.voiceAndTone ?? "" })
  const rolePromptBind   = autosave.bind("role_prompt",   { initial: agent.rolePrompt })

  // Status toggle — fires patch() imperatively (it's a button, not a text input).
  function toggleActive() {
    void autosave.patch("active", !agent.active)
    setAgent((cur) => ({ ...cur, active: !cur.active }))
  }

  function applyForgeUpdate(next: Agent) {
    setAgent(next)
    setForgeOpen(false)
  }

  function deleteAgent() {
    if (!confirm(`Delete ${agent.name}? This cannot be undone.`)) return
    router.delete(`/agents/${agent.id}`)
  }

  function copyUuid() {
    if (!agent.uuid) return
    void navigator.clipboard.writeText(agent.uuid)
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow={
          <Link href="/agents" className="hover:text-app-text">
            Agents
          </Link>
        }
        title={agent.name}
        subtitle={`All changes autosave on blur · last saved ${fmtRelativeTime(autosave.lastSavedAt ?? agent.updatedAt)}`}
        actions={
          <div className="flex items-center gap-3">
            <SaveIndicator status={autosave.status} />
            <Badge tone={agent.active ? "success" : "neutral"}>
              {agent.active ? "Active" : "Paused"}
            </Badge>
          </div>
        }
      />

      <section className="grid gap-5 lg:grid-cols-[1fr_320px]">
        {/* ── Main column ─────────────────────────────────────────── */}
        <div className="space-y-5">
          {/* Identity */}
          <Card className="space-y-4 p-6">
            <header className="flex items-center justify-between">
              <div>
                <div className="text-base font-semibold text-app-text">Identity</div>
                <div className="text-xs text-app-muted">
                  How this agent shows up to users and operators.
                </div>
              </div>
            </header>

            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Display name">
                <Input {...nameBind} />
              </Field>
              <Field label="Role">
                <Input {...roleLabelBind} placeholder="e.g. Payout specialist" />
              </Field>
              <Field label="Key">
                <code className="inline-flex items-center rounded-control border border-border bg-white/5 px-3 py-2 text-xs text-app-muted">
                  {agent.key}
                </code>
              </Field>
              <Field label="Agent UUID" className="md:col-span-2">
                <div className="flex items-center gap-2">
                  <code className="flex-1 truncate rounded-control border border-border bg-white/5 px-3 py-2 font-mono text-xs text-app-muted">
                    {agent.uuid ?? "—"}
                  </code>
                  {agent.uuid && (
                    <button
                      type="button"
                      onClick={copyUuid}
                      title="Copy UUID"
                      aria-label="Copy UUID"
                      className="rounded-control border border-border bg-white/5 p-2 text-app-subtle transition hover:bg-white/10 hover:text-app-text"
                    >
                      <Copy size={14} />
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-app-muted">
                  This is how you reference the agent in the API. Not editable.
                </p>
              </Field>
              <Field label="Avatar" className="md:col-span-2">
                <AvatarDropzone
                  agentId={agent.id}
                  agentName={agent.name}
                  avatarUrl={agent.avatarUrl ?? null}
                  onUploaded={(next) => setAgent(next)}
                />
              </Field>
              <Field label="Voice and tone" className="md:col-span-2">
                <Textarea {...voiceBind} rows={3} placeholder="Friendly, concise, never blames the user." />
              </Field>
              <Field label="Status" className="md:col-span-2">
                <button
                  type="button"
                  onClick={toggleActive}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-control border px-3 py-2 text-sm transition",
                    agent.active
                      ? "border-status-success/30 bg-status-success/10 text-status-success"
                      : "border-border bg-white/5 text-app-muted hover:bg-white/10",
                  )}
                >
                  <span className={cn("h-2 w-2 rounded-full", agent.active ? "bg-status-success" : "bg-app-subtle")} />
                  {agent.active ? "Active" : "Paused"}
                </button>
              </Field>
            </div>
          </Card>

          {/* Role prompt */}
          <Card className="space-y-3 p-6">
            <header className="flex items-start justify-between gap-4">
              <div>
                <div className="text-base font-semibold text-app-text">Role prompt</div>
                <div className="text-xs text-app-muted">
                  The system prompt this agent runs with. Edit free-form or open Forge for AI-assisted iteration.
                </div>
              </div>
              <Button variant="gold" size="sm" onClick={() => setForgeOpen(true)} className="gap-1.5">
                <Sparkles size={14} />
                Edit with Forge
              </Button>
            </header>

            <Textarea {...rolePromptBind} rows={12} className="font-mono text-xs" />

            <footer className="flex items-center justify-between text-xs text-app-muted">
              <span>Characters: {(rolePromptBind.value ?? "").length.toLocaleString()}</span>
              <span className="flex items-center gap-3">
                <span>Last updated {fmtRelativeTime(agent.updatedAt)}</span>
                <span className="text-status-success">✓ Autosaved</span>
              </span>
            </footer>
          </Card>

          {/* Skills */}
          <Card className="p-6">
            <AgentSkillsPanel
              agentId={agent.id}
              skills={skills}
              availableSkills={availableSkills}
              mode="edit"
            />
          </Card>

          {/* Runtime config (collapsed by default) */}
          <Accordion
            open={runtimeOpen}
            onToggle={() => setRuntimeOpen((v) => !v)}
            title="Runtime config"
            subtitle="Model, escalation, rate limits, greetings, auto-resolve."
          >
            <RuntimeConfigBody
              agent={agent}
              aiModels={aiModels}
              onPatch={(field, value) => {
                void autosave.patch(field, value)
                setAgent((cur) => ({ ...cur, [FIELD_TO_PROP[field] ?? field]: value } as Agent))
              }}
            />
          </Accordion>
        </div>

        {/* ── Right rail (sticky) ─────────────────────────────────── */}
        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <RehearsalCard summary={agent.rehearsalSummary} />
          <PerformanceCard agent={agent} />
          <DetailsCard agent={agent} aiModels={aiModels} />
          <DangerZoneCard agentName={agent.name} active={agent.active} onDelete={deleteAgent} />
        </aside>
      </section>

      <AgentDesignerPane
        open={forgeOpen}
        onClose={() => setForgeOpen(false)}
        agentId={agent.id}
        currentRolePrompt={agent.rolePrompt}
        onApplied={applyForgeUpdate}
      />
    </AppShell>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Sub-components — kept inline since they're tightly coupled to the page.
// ──────────────────────────────────────────────────────────────────────

function SaveIndicator({ status }: { status: AutosaveStatus }) {
  if (status === "saving") {
    return <span className="text-xs text-app-muted">Saving…</span>
  }
  if (status === "saved") {
    return <span className="text-xs text-status-success">✓ Saved</span>
  }
  if (status === "error") {
    return <span className="text-xs text-status-error">Save failed — retry on blur</span>
  }
  return <span className="text-xs text-app-subtle">All changes saved</span>
}

function Field({
  label,
  children,
  className,
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-xs font-medium text-app-muted">{label}</span>
      {children}
    </label>
  )
}

function Accordion({
  open,
  onToggle,
  title,
  subtitle,
  children,
}: {
  open: boolean
  onToggle: () => void
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left transition hover:bg-white/5"
      >
        <div>
          <div className="text-base font-semibold text-app-text">{title}</div>
          {subtitle && <div className="text-xs text-app-muted">{subtitle}</div>}
        </div>
        {open ? <ChevronDown size={16} className="text-app-muted" /> : <ChevronRight size={16} className="text-app-muted" />}
      </button>
      {open && <div className="border-t border-border px-6 py-5">{children}</div>}
    </Card>
  )
}

// Maps the snake_case `field` key autosaved to the API onto the camelCase
// prop on the local Agent state mirror. Anything not listed flows through
// untouched (useful for fields whose API key already matches the prop name).
const FIELD_TO_PROP: Record<string, keyof Agent> = {
  ai_model_id: "aiModelId",
  human_escalation_enabled: "humanEscalationEnabled",
  auto_approve_escalation_categories: "autoApproveEscalationCategories",
  auto_resolve_after_hours: "autoResolveAfterHours",
  rate_limits: "rateLimits",
  greeting_messages: "greetings",
}

// Human-readable label per field used in the post-save toast. Keep these
// short — the toast is confirmation, not narration.
const FIELD_LABELS: Record<string, string> = {
  name: "Name updated",
  role_label: "Role updated",
  voice_and_tone: "Voice and tone updated",
  role_prompt: "Role prompt updated",
  active: "Status updated",
  ai_model_id: "AI model updated",
  human_escalation_enabled: "Human escalation updated",
  auto_approve_escalation_categories: "Auto-approve setting updated",
  rate_limits: "Rate limits updated",
  greeting_messages: "Greetings updated",
  auto_resolve_after_hours: "Auto-resolve updated",
}

function RuntimeConfigBody({
  agent,
  aiModels,
  onPatch,
}: {
  agent: Agent
  aiModels: AiModelOption[]
  onPatch: (field: string, value: unknown) => void
}) {
  return (
    <div className="space-y-5">
      <Field label="AI model">
        {aiModels.length > 0 ? (
          <select
            defaultValue={agent.aiModelId ?? ""}
            onChange={(e) => {
              const v = e.target.value === "" ? null : Number(e.target.value)
              onPatch("ai_model_id", v)
            }}
            className="rounded-control border border-border bg-white/5 px-3 py-2 text-sm text-app-text outline-none transition focus:border-brand-red/50"
          >
            <option value="">— Use company default —</option>
            {aiModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.provider ? ` (${m.provider})` : ""}
              </option>
            ))}
          </select>
        ) : (
          <code className="inline-flex items-center rounded-control border border-border bg-white/5 px-3 py-2 text-xs text-app-muted">
            {agent.aiModelId ?? "default"}
          </code>
        )}
      </Field>

      <Field label="Human escalation">
        <button
          type="button"
          onClick={() => onPatch("human_escalation_enabled", !agent.humanEscalationEnabled)}
          className={cn(
            "inline-flex items-center gap-2 rounded-control border px-3 py-2 text-sm transition",
            agent.humanEscalationEnabled
              ? "border-status-success/30 bg-status-success/10 text-status-success"
              : "border-border bg-white/5 text-app-muted hover:bg-white/10",
          )}
        >
          <span
            className={cn(
              "h-2 w-2 rounded-full",
              agent.humanEscalationEnabled ? "bg-status-success" : "bg-app-subtle",
            )}
          />
          {agent.humanEscalationEnabled ? "Enabled" : "Disabled"}
        </button>
      </Field>

      <Field label="Auto-approve LLM-proposed escalation categories">
        <button
          type="button"
          onClick={() =>
            onPatch(
              "auto_approve_escalation_categories",
              !agent.autoApproveEscalationCategories,
            )
          }
          className={cn(
            "inline-flex items-center gap-2 rounded-control border px-3 py-2 text-sm transition",
            agent.autoApproveEscalationCategories
              ? "border-status-success/30 bg-status-success/10 text-status-success"
              : "border-border bg-white/5 text-app-muted hover:bg-white/10",
          )}
        >
          <span
            className={cn(
              "h-2 w-2 rounded-full",
              agent.autoApproveEscalationCategories ? "bg-status-success" : "bg-app-subtle",
            )}
          />
          {agent.autoApproveEscalationCategories ? "Auto-approve on" : "Review every proposal"}
        </button>
        <span className="text-xs text-app-muted">
          When on, the classifier promotes new categories straight to your live catalog. Turn off if you want to review every proposal before it counts.
        </span>
      </Field>

      <RateLimitsFields
        rateLimits={(agent.rateLimits ?? {}) as Record<string, unknown>}
        onPatch={onPatch}
      />

      <GreetingsField
        greetings={agent.greetings ?? []}
        onPatch={(next) => onPatch("greeting_messages", next)}
      />

      <AutoResolveField
        hours={agent.autoResolveAfterHours}
        onPatch={(next) => onPatch("auto_resolve_after_hours", next)}
      />
    </div>
  )
}

// Structured rate-limit form. Each row is a numeric input with the platform
// default shown as placeholder. Empty input = clear the override (falls
// back to default). Other keys present in `rate_limits` (turns_per_convo,
// files_per_message, etc — set via ActiveAdmin) are preserved on save.
function RateLimitsFields({
  rateLimits,
  onPatch,
}: {
  rateLimits: Record<string, unknown>
  onPatch: (field: string, value: unknown) => void
}) {
  const [overrides, setOverrides] = useState<Record<string, string>>(() =>
    (Object.keys(RATE_LIMIT_DEFAULTS) as RateLimitKey[]).reduce(
      (acc, k) => {
        const v = rateLimits[k]
        acc[k] = typeof v === "number" ? String(v) : ""
        return acc
      },
      {} as Record<string, string>,
    ),
  )

  function commit(key: RateLimitKey, raw: string) {
    const next = { ...rateLimits }
    if (raw.trim() === "") {
      delete next[key]
    } else {
      const n = Number(raw)
      if (!Number.isFinite(n)) return
      next[key] = Math.max(0, Math.floor(n))
    }
    onPatch("rate_limits", next)
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-semibold text-app-text">Rate limits</div>
        <div className="text-xs text-app-muted">
          Per end-user (the <code className="rounded bg-white/5 px-1 py-0.5">source_key</code>{" "}
          from the JWT) per agent. Leave a field blank to inherit the platform default.
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {(Object.keys(RATE_LIMIT_DEFAULTS) as RateLimitKey[]).map((k) => (
          <Field key={k} label={RATE_LIMIT_LABELS[k]}>
            <Input
              type="number"
              min={0}
              inputMode="numeric"
              value={overrides[k] ?? ""}
              placeholder={RATE_LIMIT_DEFAULTS[k].toLocaleString()}
              onChange={(e) =>
                setOverrides((o) => ({ ...o, [k]: e.target.value }))
              }
              onBlur={() => commit(k, overrides[k] ?? "")}
            />
          </Field>
        ))}
      </div>
    </div>
  )
}

const RATE_LIMIT_LABELS: Record<RateLimitKey, string> = {
  messages_per_minute: "Messages per minute",
  messages_per_hour: "Messages per hour",
  convos_per_day: "Conversations per day",
  daily_token_budget: "Tokens per day",
}

// Tokenized chip input for `config.greeting_messages`. Type a phrase and
// press Enter (or comma) to commit it as a chip; backspace on an empty
// input removes the last chip; the X on each chip removes that one. All
// edits autosave on each commit.
function GreetingsField({
  greetings,
  onPatch,
}: {
  greetings: string[]
  onPatch: (next: string[]) => void
}) {
  const [draft, setDraft] = useState("")

  function add() {
    const trimmed = draft.trim()
    if (!trimmed) return
    if (greetings.includes(trimmed)) {
      setDraft("")
      return
    }
    setDraft("")
    onPatch([...greetings, trimmed])
  }

  function remove(i: number) {
    const next = greetings.slice()
    next.splice(i, 1)
    onPatch(next)
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      add()
      return
    }
    if (e.key === "Backspace" && draft === "" && greetings.length > 0) {
      e.preventDefault()
      remove(greetings.length - 1)
    }
  }

  return (
    <Field label="Greetings">
      <div className="flex flex-wrap items-center gap-2 rounded-control border border-border bg-white/5 px-2 py-2">
        {greetings.map((g, i) => (
          <span
            key={`${g}-${i}`}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-white/10 px-2.5 py-1 text-xs text-app-text"
          >
            {g}
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label={`Remove greeting ${g}`}
              className="text-app-subtle transition hover:text-app-text"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={add}
          placeholder={greetings.length === 0 ? "Hi! How can I help?" : "Add another…"}
          className="min-w-[12ch] flex-1 bg-transparent px-1 py-0.5 text-sm text-app-text outline-none placeholder:text-app-subtle"
        />
      </div>
      <span className="text-xs text-app-muted">
        One picked at random when a conversation starts. Press Enter or comma to add.
      </span>
    </Field>
  )
}

// Number input for `config.auto_resolve_after_hours`. 0 disables the
// idle sweep entirely.
function AutoResolveField({
  hours,
  onPatch,
}: {
  hours: number
  onPatch: (next: number) => void
}) {
  const [value, setValue] = useState<string>(String(hours ?? 0))

  function commit() {
    const n = Number(value)
    if (!Number.isFinite(n)) return
    const next = Math.max(0, Math.floor(n))
    if (next !== hours) onPatch(next)
  }

  return (
    <Field label="Auto-resolve after (hours)">
      <Input
        type="number"
        min={0}
        inputMode="numeric"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        className="max-w-[10rem]"
      />
      <span className="text-xs text-app-muted">
        Idle conversations close automatically after this many hours. Set to 0 to disable.
      </span>
    </Field>
  )
}

// ── Right rail cards ──────────────────────────────────────────────────

function RehearsalCard({ summary }: { summary: Agent["rehearsalSummary"] }) {
  return (
    <Card className="space-y-3 p-5">
      <div className="text-sm font-semibold text-app-text">Rehearsal status</div>
      {summary ? (
        <>
          <Badge tone={rehearsalTone(summary.state)}>{prettyState(summary.state)}</Badge>
          <div className="text-sm text-app-muted">
            {summary.passedCount}/{summary.total} scenarios passed
          </div>
          <Link
            href={`/testing#rehearsal-${summary.id}`}
            className="inline-flex items-center gap-1 text-xs text-vip-gold transition hover:text-vip-goldSoft"
          >
            Open Test Lab
            <ExternalLink size={12} />
          </Link>
        </>
      ) : (
        <>
          <div className="text-sm text-app-muted">No rehearsal yet.</div>
          <Link
            href="/testing"
            className="inline-flex items-center gap-1 text-xs text-vip-gold transition hover:text-vip-goldSoft"
          >
            Run rehearsal
            <ExternalLink size={12} />
          </Link>
        </>
      )}
    </Card>
  )
}

function rehearsalTone(state: string): "success" | "warning" | "error" | "neutral" {
  switch (state) {
    case "passed":
      return "success"
    case "running":
    case "pending":
      return "warning"
    case "failed":
    case "errored":
      return "error"
    default:
      return "neutral"
  }
}

function prettyState(state: string): string {
  if (!state) return "Unknown"
  return state.charAt(0).toUpperCase() + state.slice(1)
}

function PerformanceCard({ agent }: { agent: Agent }) {
  return (
    <Card className="space-y-3 p-5">
      <div className="text-sm font-semibold text-app-text">Performance (30d)</div>
      <Row label="Tickets handled" value={agent.ticketsHandled.toLocaleString()} />
      <Row label="Quality score" value={`${agent.qualityScore}/100`} />
      <Row label="Open ticket load" value={agent.openTicketLoad.toLocaleString()} />
      <Link
        href={`/analytics?agent=${agent.id}`}
        className="inline-flex items-center gap-1 text-xs text-vip-gold transition hover:text-vip-goldSoft"
      >
        View analytics
        <ExternalLink size={12} />
      </Link>
    </Card>
  )
}

function DetailsCard({
  agent,
  aiModels,
}: {
  agent: Agent
  aiModels: AiModelOption[]
}) {
  const aiModelName =
    agent.aiModelId == null
      ? "default"
      : (aiModels.find((m) => m.id === agent.aiModelId)?.name ?? `#${agent.aiModelId}`)

  return (
    <Card className="space-y-3 p-5">
      <div className="text-sm font-semibold text-app-text">Details</div>
      <Row label="Created" value={fmtRelativeTime(agent.createdAt)} />
      <Row label="Updated" value={fmtRelativeTime(agent.updatedAt)} />
      <Row label="AI model" value={aiModelName} />
      <Row label="Human escalation" value={agent.humanEscalationEnabled ? "Yes" : "No"} />
    </Card>
  )
}

function DangerZoneCard({
  agentName,
  active,
  onDelete,
}: {
  agentName: string
  active: boolean
  onDelete: () => void
}) {
  return (
    <Card className="space-y-3 border-brand-red/40 p-5">
      <div className="text-sm font-semibold text-brand-red">Danger zone</div>
      <p className="text-xs text-app-muted">
        Deleting an agent removes its skills, conversations, and rehearsal history.
        {active && " Pause the agent first — toggle Active off in Identity."}
      </p>
      <Button
        variant="red"
        size="sm"
        onClick={onDelete}
        disabled={active}
        className="gap-1.5"
        title={active ? "Pause the agent before deleting" : undefined}
      >
        <Trash2 size={14} />
        Delete {agentName}
      </Button>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-app-muted">{label}</span>
      <span className="text-app-text">{value}</span>
    </div>
  )
}
